import { Address } from '~shared/shared.types';

export interface Company {
	name: string;
	companyClass: 'BV' | 'NV' | 'VOF' | 'EZ';
	vatNumber: string;
	address: Address;
}

/**
 * Whether the customer is a trade professional, chosen at signup and stored by the account
 * service (mirrored to Odoo as the partner's customer type). A professional installs cabinets
 * for clients of their own and gets the end-client quotation module on top of our quote; an
 * individual buys for their own use — also when ordering through a company.
 */
export enum AccountType {
	INDIVIDUAL = 'INDIVIDUAL',
	PROFESSIONAL = 'PROFESSIONAL'
}

/** Who the invoice is made out to; decides whether company details are asked. */
export enum BillingType {
	PERSON = 'PERSON',
	COMPANY = 'COMPANY'
}

/** A professional always invoices to a company; an individual does when they choose to. */
export const invoicesToCompany = (accountType: AccountType, billingType: BillingType): boolean =>
	accountType === AccountType.PROFESSIONAL || billingType === BillingType.COMPANY;

export enum DeliverableAddressType {
	SAME = 'SAME',
	OTHER = 'OTHER'
}

/** PDF template a reseller's quotations are rendered with. */
export enum DocumentStyle {
	MODERN = 'MODERN',
	TRADITIONAL = 'TRADITIONAL',
	BOLD = 'BOLD'
}

/**
 * Account-level white-label settings for the rendered quotation PDF: brand colours
 * (hex ^#[0-9A-Fa-f]{6}$), default intro/outro HTML the backend substitutes when a
 * quotation leaves its own text empty, and the chosen document template. All optional.
 */
export interface QuotationSettings {
	primaryColor?: string;
	secondaryColor?: string;
	defaultTextBefore?: string;
	defaultTextAfter?: string;
	defaultTextTerms?: string;
	documentStyle?: DocumentStyle;
	// Account-level defaults for the per-quote settings (each overridable per quote).
	defaultMarkupPct?: number;
	defaultVatRate?: number;
	itemBreakdown?: boolean;
	showServices?: boolean;
}

export interface BaseAccount {
	id: string;
	accountType: AccountType;
	deliveryDetails: Address;
	name: string;
	overwritten?: boolean;
	company?: Company;
	invoiceAddress: Address;
	generalEmail?: string;
	invoiceEmail?: string;
	quotationSettings?: QuotationSettings;
}
